import { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";

import { useMedplum } from "@medplum/react";

import { Box, Button, Collapse } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";

import { ProfileContext } from "@/contexts/profile";
import { useIsBelowBreakpoint } from "@/hooks/useBreakpoint";
import { fetchChecklists } from "@/services/checklist";
import { useAppDispatch } from "@/store";
import { loadChecklists } from "@/store/slices/checklists";
import { ChecklistTask } from "@/types/checklist-task";

import ClientHealthSummary from "./ClientHealthSummary/ClientHealthSummary";
import ClientInformationSummary from "./ClientInformationSummary";
import { ClientConfidentialNote } from "./ClientNotes/ClientConfidentialNote";
import ClientPregnancyStatus from "./ClientPregnancyStatus";

export default function ClientProfile() {
  const { isPatient, profile } = useContext(ProfileContext);
  const dispatch = useAppDispatch();
  const medplum = useMedplum();
  const { clientId } = useParams() as { clientId: string };
  const isStacked = useIsBelowBreakpoint("lg");
  const [isDetailsOpen, { toggle: toggleDetails }] = useDisclosure(false);

  useEffect(() => {
    (async () => {
      const fetchedChecklists = await fetchChecklists(medplum, clientId);
      dispatch(loadChecklists(fetchedChecklists as ChecklistTask[]));
    })();
  }, [clientId, dispatch, isPatient, medplum, profile?.id]);

  // Below lg the profile stacks above the chart tabs, so everything past the client's identity
  // starts collapsed to keep the tabs close to the top of the page.
  return (
    <Box w={{ base: "100%", lg: 360 }} data-testid="patient-profile">
      <ClientInformationSummary />
      {isStacked && (
        <Button
          mih={44}
          fullWidth
          variant="subtle"
          onClick={toggleDetails}
          aria-expanded={isDetailsOpen}
          data-testid="client-profile-details-toggle"
          rightSection={isDetailsOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
        >
          {isDetailsOpen ? "Hide details" : "Show details"}
        </Button>
      )}
      {/* One tree at every width, so crossing lg never remounts these sections. */}
      <Collapse in={!isStacked || isDetailsOpen}>
        <ClientPregnancyStatus />
        <ClientHealthSummary />
        {!isPatient && <ClientConfidentialNote />}
      </Collapse>
    </Box>
  );
}
